/**
 * 司機服務
 * 統一處理司機相關 API，支援 Mock/真實 API 切換
 */

import { config } from './config'
import { mockGetDriver, mockBindDriver } from '@/data/mockData'
import { bindDriver as realBindDriver, getMyDriverInfo } from './driverAuthService'
import apiClient from './apiClient'
import type { Driver, BindDriverResponse, MockDriver } from '@/types'

/**
 * 取得當前司機資料
 * @param lineUserId LINE 使用者 ID（Mock 模式使用）
 */
export async function getDriver(lineUserId: string): Promise<Driver | null> {
  if (config.useMock) {
    console.log('[DriverService] 使用 Mock: getDriver')
    const mockDriver = await mockGetDriver(lineUserId)
    return mockDriver ? convertMockDriverToDriver(mockDriver) : null
  }

  console.log('[DriverService] 呼叫 API: getDriver')
  try {
    // getMyDriverInfo 會自動使用儲存的系統 JWT（LINE_USER 或 DRIVER 類型）
    // 不需要傳入 LINE ID Token
    const driverInfo = await getMyDriverInfo()
    return driverInfo
  } catch (error) {
    console.error('[DriverService] getDriver 失敗:', error)
    return null
  }
}

/**
 * 將 Mock Driver 轉換為實際 Driver 型別
 */
function convertMockDriverToDriver(mock: MockDriver): Driver {
  return {
    driverId: mock.driverId,
    driverName: mock.name,
    companyCode: mock.companyCode,
    companyName: mock.companyName,
    status: mock.status.toUpperCase(),
    statusName: mock.status === 'active' ? '可出勤' : '停用',
    hasLineBound: true,
    currentVehicleNo: mock.vehicleNumber || null,
    hasUnfinishedDuty: false, // Mock 預設無未交班記錄
  }
}

/**
 * 綁定司機帳號
 * @param driverId 司機編號
 * @param verificationCode 驗證碼
 * @param lineUserId LINE 使用者 ID（Mock 模式使用）
 */
export async function bindDriverAccount(
  driverId: string,
  verificationCode: string,
  lineUserId: string,
  companyCode?: string
): Promise<BindDriverResponse> {
  if (config.useMock) {
    console.log('[DriverService] 使用 Mock: bindDriver')
    return mockBindDriver(driverId, verificationCode, lineUserId)
  }

  console.log('[DriverService] 呼叫 API: bindDriver')
  // 使用儲存的系統 JWT（在 lineLogin 時已儲存）
  const result = await realBindDriver(driverId, verificationCode, companyCode)

  if (result.success && result.driver) {
    return {
      success: true,
      message: '綁定成功',
      driver: {
        id: result.driver.driverId,
        driverId: result.driver.driverId,
        name: result.driver.driverName,
        companyName: result.driver.companyName,
      },
    }
  }

  return {
    success: false,
    message: result.message,
  }
}

/**
 * 解除 LINE 綁定結果
 */
export interface UnbindLineResult {
  success: boolean
  message: string
}

/**
 * 解除 LINE 綁定
 * @param driverId 司機編號
 */
export async function unbindLine(driverId: string): Promise<UnbindLineResult> {
  if (config.useMock) {
    console.log('[DriverService] 使用 Mock: unbindLine')
    // Mock 模式：模擬成功
    return {
      success: true,
      message: '解除綁定成功',
    }
  }

  console.log('[DriverService] 呼叫 API: unbindLine, driverId:', driverId)
  try {
    // 使用 LINE API 端點（/api/line/drivers/unbind）
    // 此端點接受 DRIVER token，會自動透過 LINE ID 找到對應司機並解綁
    await apiClient.delete('/line/drivers/unbind')
    return {
      success: true,
      message: '解除綁定成功',
    }
  } catch (error) {
    console.error('[DriverService] unbindLine 失敗:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : '解除綁定失敗',
    }
  }
}
